/**
 * Turning a file the browser handed us into what the API accepts.
 *
 * ## Read the bytes, never the text
 *
 * Both upload endpoints take the file base64-encoded in a JSON body, and both
 * mean *the file exactly as it was*. `FileReader.readAsText` and `File.text()`
 * decode as UTF-8 and are therefore the wrong tool twice over:
 *
 * - A Danske Bank statement is **ISO-8859-1**. Decoding it as UTF-8 replaces
 *   every æ, ø and å with U+FFFD, and re-encoding that as UTF-8 produces a file
 *   the reader will refuse — or worse, one it accepts with mangled payee names
 *   that are then bookkeeping records.
 * - A PDF or a JPEG is not text in any encoding. Round-tripping it through a
 *   string corrupts it byte for byte.
 *
 * `arrayBuffer()` returns the bytes as they are on disk. That is what gets
 * encoded, so what the server stores is what the bank exported — which is the
 * whole point of `docs/reference/bank.md`'s instruction to pass the file
 * unmodified.
 */

/**
 * Base64 for a byte array.
 *
 * Chunked, because `String.fromCharCode(...bytes)` spreads every byte into an
 * argument list: a 25 MB attachment is 25 million arguments and overflows the
 * call stack. The chunk size is well under any engine's argument limit.
 */
export function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    const chunk = bytes.subarray(offset, offset + CHUNK);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

/** A file's bytes, base64-encoded, unmodified. */
export async function fileToBase64(file: File): Promise<string> {
  return bytesToBase64(new Uint8Array(await file.arrayBuffer()));
}

/**
 * A file size for a person: `"1,2 MB"`.
 *
 * A byte count is a `number` and stays one — this is a file length, not money,
 * and the decimal.js rule is about amounts that reach the ledger.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }
  const units = ['kB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal, Danish separator, to match every other number on screen.
  return `${value.toFixed(1).replace('.', ',')} ${units[unit] ?? 'kB'}`;
}
